import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getAdminAiSpend } from "@/api/admin";
import { Card, SectionLabel } from "@/components/ui/Card";

export default function AdminAiSpend() {
  const [days, setDays] = useState(30);
  const q = useQuery({
    queryKey: ["admin-ai-spend", days],
    queryFn: () => getAdminAiSpend(days),
    refetchInterval: 60_000,
  });

  if (q.isLoading) return <div className="py-6 text-center text-taupe">Loading…</div>;
  if (!q.data) return <div className="py-6 text-center text-coral">Failed to load.</div>;

  const d = q.data;

  // Daily bar chart — compute daily totals (summing over models)
  const dailyTotals = new Map<string, { usd: number; calls: number }>();
  for (const row of d.daily_timeseries) {
    const ex = dailyTotals.get(row.date) || { usd: 0, calls: 0 };
    ex.usd += row.usd;
    ex.calls += row.calls;
    dailyTotals.set(row.date, ex);
  }
  const dailyArr = [...dailyTotals.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1));
  const maxDaily = dailyArr.reduce((m, [, v]) => Math.max(m, v.usd), 0.0001);

  // operation × model matrix
  const ops = new Map<string, Record<string, { usd: number; calls: number }>>();
  const modelsSet = new Set<string>();
  for (const r of d.by_operation_model) {
    modelsSet.add(r.model);
    const row = ops.get(r.operation) || {};
    row[r.model] = { usd: r.usd, calls: r.calls };
    ops.set(r.operation, row);
  }
  const models = [...modelsSet].sort();

  return (
    <div className="space-y-5">
      <Card className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <SectionLabel>Period</SectionLabel>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="mt-1 rounded-md border border-cream-border bg-cream-light px-3 py-2 text-[13px]"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 180 days</option>
          </select>
        </div>
        <div className="flex gap-4 text-right">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-taupe">
              Total
            </div>
            <div className="metric-number text-[22px] text-amber-dark">
              ${d.total_period_usd.toFixed(4)}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-taupe">
              Failed
            </div>
            <div className="metric-number text-[22px]">
              {d.failed_calls_period}
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <SectionLabel>Daily spend (USD)</SectionLabel>
        {dailyArr.length === 0 ? (
          <div className="mt-3 py-6 text-center text-taupe">No calls yet.</div>
        ) : (
          <div className="mt-4 flex items-end gap-1">
            {dailyArr.map(([date, v]) => {
              const h = Math.max(4, Math.round((v.usd / maxDaily) * 100));
              return (
                <div
                  key={date}
                  className="flex-1"
                  title={`${date}: $${v.usd.toFixed(4)} · ${v.calls} calls`}
                >
                  <div
                    className="rounded-t bg-amber/50"
                    style={{ height: `${h}px`, minHeight: "4px" }}
                  />
                </div>
              );
            })}
          </div>
        )}
        {dailyArr.length > 0 ? (
          <div className="mt-1 flex justify-between text-[10px] text-taupe">
            <span>{dailyArr[0][0]}</span>
            <span>{dailyArr[dailyArr.length - 1][0]}</span>
          </div>
        ) : null}
      </Card>

      <Card>
        <SectionLabel>Top 10 users this month</SectionLabel>
        <table className="mt-3 w-full text-[13px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-taupe">
              <th className="py-1">Email</th>
              <th className="py-1 text-right">USD</th>
              <th className="py-1 text-right">Calls</th>
            </tr>
          </thead>
          <tbody>
            {d.top_users_this_month.length === 0 ? (
              <tr>
                <td colSpan={3} className="py-2 text-taupe">
                  No calls this month.
                </td>
              </tr>
            ) : (
              d.top_users_this_month.map((r) => (
                <tr key={r.user_id} className="border-t border-cream-border">
                  <td className="py-1.5">
                    <a
                      href={`/admin/users/${r.user_id}`}
                      className="text-navy-deep hover:text-amber-dark"
                    >
                      {r.email}
                    </a>
                  </td>
                  <td className="metric-number py-1.5 text-right">
                    ${r.usd.toFixed(4)}
                  </td>
                  <td className="metric-number py-1.5 text-right text-taupe">
                    {r.calls}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>

      <Card>
        <SectionLabel>Operation × model</SectionLabel>
        <table className="mt-3 w-full text-[13px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-taupe">
              <th className="py-1">Operation</th>
              {models.map((m) => (
                <th key={m} className="py-1 text-right">
                  {m.replace("claude-", "").replace("-20251001", "")}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...ops.keys()].sort().map((op) => (
              <tr key={op} className="border-t border-cream-border">
                <td className="py-1.5">{op}</td>
                {models.map((m) => {
                  const cell = ops.get(op)?.[m];
                  return (
                    <td
                      key={m}
                      className="metric-number py-1.5 text-right text-taupe"
                    >
                      {cell ? `$${cell.usd.toFixed(4)}` : "—"}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
